import React, { useState, useEffect, useContext, useRef } from "react";
import { SocketContext } from "../contexts/SocketContext";
import { jwtDecode } from "jwt-decode";
import "./Chat.scss";

const Chat = () => {
    const { socket, isConnected, sendMessage, deleteMessage } = useContext(SocketContext);
    const [messages, setMessages] = useState([]);
    const [text, setText] = useState("");
    const [error, setError] = useState(null);
    const messagesEndRef = useRef(null);

    // JWT 토큰에서 userId 가져오기
    let userId;
    try {
        const token = localStorage.getItem("token");
        if (token) {
            const decoded = jwtDecode(token);
            userId = decoded.userId; // MongoDB ObjectId
        }
    } catch (err) {
        console.error("유효하지 않은 JWT 토큰:", err);
    }

    // 소켓 이벤트 설정
    useEffect(() => {
        if (!socket || !userId) return;

        const handleInitMessages = (msgs) => {
            console.log("초기 메시지 수신:", JSON.stringify(msgs, null, 2)); // 자세한 구조 로깅
            setMessages(msgs);
        };
        const handleReceiveMessage = (msg) => {
            console.log("메시지 수신:", JSON.stringify(msg, null, 2));
            setMessages((prev) => [...prev, msg]);
        };
        const handleMessageDeleted = (deletedId) => {
            console.log("메시지 삭제됨:", deletedId);
            setMessages((prev) => prev.filter((m) => m._id !== deletedId));
        };
        const handleError = (err) => {
            console.error("소켓 에러:", err);
            setError(err.message || "오류가 발생했습니다");
        };

        socket.on("initMessages", handleInitMessages);
        socket.on("receiveMessage", handleReceiveMessage);
        socket.on("messageDeleted", handleMessageDeleted);
        socket.on("error", handleError);

        // 방에 입장
        socket.emit("joinRoom", { roomId: "general", userId }, (response) => {
            if (response?.error) {
                console.error("방 입장 실패:", response.error);
                setError("방에 입장하지 못했습니다");
            } else {
                console.log("방 입장 성공: general");
            }
        });

        return () => {
            socket.off("initMessages", handleInitMessages);
            socket.off("receiveMessage", handleReceiveMessage);
            socket.off("messageDeleted", handleMessageDeleted);
            socket.off("error", handleError);
        };
    }, [socket, userId]);

    // 최신 메시지로 자동 스크롤
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    const handleSend = () => {
        if (!isConnected) {
            setError("서버에 연결되지 않아 메시지를 보낼 수 없습니다");
            return;
        }
        if (!text.trim()) {
            setError("메시지는 비어 있을 수 없습니다");
            return;
        }
        sendMessage({ roomId: "general", userId, message: text });
        setText("");
        setError(null);
    };

    const handleDelete = (id) => {
        if (!isConnected) {
            setError("서버에 연결되지 않아 메시지를 삭제할 수 없습니다");
            return;
        }
        if (window.confirm("정말 삭제할까요?")) {
            deleteMessage(id, "general");
            setError(null);
        }
    };

    // 인증되지 않은 경우 로그인 프롬프트 표시
    if (!userId) {
        return (
            <div className="chat-container">
                <div className="chat-header">
                    <h2># 일반 채팅</h2>
                </div>
                <div className="chat-messages">
                    <p>채팅에 참여하려면 로그인하세요.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="chat-container">
            <div className="chat-header">
                <h2># 일반 채팅 {isConnected ? "(연결됨)" : "(연결 끊김)"}</h2>
            </div>
            {error && (
                <div className="chat-error" style={{ color: "red", textAlign: "center" }}>
                    {error}
                </div>
            )}
            <div className="chat-messages">
                {messages.map((msg) => (
                    <div
                        key={msg._id}
                        className={`message-bubble ${
                            msg.userId?._id === userId ? "my-message" : ""
                        }`} // msg.userId 유효성 확인
                    >
                        <div className="message-user">
                            {msg.userId?.nickname || msg.userId?.email || "익명"}
                        </div>
                        <div className="message-text">{msg.message}</div>
                        {msg.userId?._id === userId && (
                            <button
                                className="delete-btn"
                                onClick={() => handleDelete(msg._id)}
                            >
                                삭제
                            </button>
                        )}
                    </div>
                ))}
                <div ref={messagesEndRef} />
            </div>
            <div className="chat-input">
                <input
                    value={text}
                    onChange={(e) => {
                        setText(e.target.value);
                        setError(null);
                    }}
                    onKeyDown={(e) => e.key === "Enter" && handleSend()}
                    placeholder="메시지를 입력하세요"
                    disabled={!isConnected}
                />
                <button onClick={handleSend} disabled={!isConnected}>
                    전송
                </button>
            </div>
        </div>
    );
};

export default Chat;